import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/others/Card.css";
import { apiCall } from "./ApiCall";
import ToastPopup from "../popups/ToastPopup";
import EncryptionUtils from "./encryptionUtils";
import { createToastMessage, ToastMessage } from "../interFaces/interfaces";
const themeLabels: Record<string, string> = { bgColor: "Background Color", gradientbg: "Background Inner Color", textColor: "Text Color", iconColor: "Icon Color", buttonBg: "Button Background Color", buttonText: "Button Text Color", tableBgColor: "Table Background Color", tableBodyTextColor: "Table Body Text Color", tableHeaderBgColor: "Table Header Background Color", tableHeaderTextColor: "Table Header Text Color", cardBg: "Card Background Color", cardInnerBg: "Card Inner Background Color", cardText: "Card Text Color" };
const defaultTheme = { bgColor: "#032c5a", textColor: "#a3ddf1", iconColor: "#000000", buttonBg: "#a3ddf1", buttonText: "#000000", tableBgColor: "#fffbea", tableBodyTextColor: "#000000", tableHeaderBgColor: "#032c5a", tableHeaderTextColor: "#ffffff", cardBg: "#032c5a", cardInnerBg: "#ffffff", cardText: "#333333", gradientbg: "#e7d9d9" };
const CustomTheme: React.FC = () => {
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const userId = userInfo?.id;
  const [theme, setTheme] = useState<Record<string, string>>(defaultTheme);
  const [initialTheme, setInitialTheme] = useState(defaultTheme);
  const [unsaved, setUnsaved] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const updateCSSVariables = (themeObj: Record<string, string>) => {
    document.documentElement.removeAttribute("data-theme");
    for (const [key, value] of Object.entries(themeObj)) {
      const cssVariable = `--custom-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
      document.documentElement.style.setProperty(cssVariable, value);
    }
    document.documentElement.setAttribute("data-theme", "custom");
  };
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const data = await apiCall("get", `/api/themes/${userId}`);
        const fetchedTheme = data.theme || data;
        if (fetchedTheme && Object.keys(fetchedTheme).length > 0) {
          setTheme(fetchedTheme);
          setInitialTheme(fetchedTheme);
          updateCSSVariables(fetchedTheme);
        } else {
          setTheme(defaultTheme);
          setInitialTheme(defaultTheme);
          updateCSSVariables(defaultTheme);
        }
      } catch {
        setTheme(defaultTheme);
        setInitialTheme(defaultTheme);
        updateCSSVariables(defaultTheme);
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, [userId]);
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsaved]);
  const handleChange = (key: string, value: string) => {
    const updatedTheme = { ...theme, [key]: value };
    setTheme(updatedTheme);
    setUnsaved(true);
  };
  const handleReset = () => {
    setTheme(defaultTheme);
    setUnsaved(true);
  };
  const handleSubmit = async () => {
    try {
      await apiCall("post", `/api/themes/${userId}`, theme);
      updateCSSVariables(theme);
      localStorage.setItem("userTheme", JSON.stringify(theme));
      localStorage.setItem("theme", "custom");
      setMessage(createToastMessage("Theme saved successfully!", "success", 3000));
      setUnsaved(false);
      setTimeout(() => navigate("/dashboard"), 3000);
    } catch {
      setMessage(createToastMessage("Error applying theme", "error", 3000));
    }
  };
  const handleBack = () => {
    if (unsaved) {
      setShowConfirm(true);
    } else {
      navigate(-1);
    }
  };
  const handleLeaveWithoutSaving = () => {
    updateCSSVariables(initialTheme);
    setShowConfirm(false);
    navigate(-1);
  };
  return (
    <div className="container">
      <div className="inner-container">
        <h1 className="title">Customize Your Theme</h1>
        <div className="button-group">
          <button onClick={handleReset} className="reset-button">Default Theme</button>
        </div>
      </div>
      <div className="grid">
        {loading ? (
          <div className="loading-message">Loading theme...</div>
        ) : (
          Object.entries(theme).map(([key, value]) => (
            <div className="card" key={key}>
              <div className="card-inner">
                <h3>{themeLabels[key] || key}</h3>
                <label className="color-picker">
                  <input type="color" value={value} onChange={(e) => handleChange(key, e.target.value)} />
                  <span className="color-code">{value}</span>
                </label>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="buttons">
        <button onClick={handleBack} className="back-button">Back</button>
        <button onClick={handleSubmit} className="back-button">Submit</button>
      </div>
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Unsaved Changes</h2>
            <p>You have unsaved changes. Leave without saving?</p>
            <div className="popup-buttons">
              <button onClick={() => setShowConfirm(false)} className="cancelbutton">Cancel</button>
              <button onClick={handleLeaveWithoutSaving} className="savebutton">Leave Without Saving</button>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CustomTheme;